<template>
  <div class="videoCall">
    <div id="mini-videos">
      <video
        v-if="!localOnlyAudio"
        class="mini-video"
        id="localVideo"
        autoplay
        playsinline
        muted
      />
      <div v-if="localOnlyAudio" class="mini-audio">
        <div class="miniOnlyAudio">
          <p>{{ $t('message.videoCall.localTag') }}</p>
          <v-icon large color="red">mdi-video-off</v-icon>
        </div>
        <audio class="mini-video" id="localVideo" autoplay playsinline muted />
      </div>
      <video
        id="otherVideo"
        v-if="!otherOnlyAudio"
        v-show="videoElements.otherVideo"
        class="mini-video"
        autoplay
        playsinline
      />
      <div v-if="otherOnlyAudio" class="mini-audio">
        <div class="miniOnlyAudio">
          <p>{{ onlyAudioMessage('otherVideo') }}</p>
          <v-icon large color="red">mdi-video-off</v-icon>
        </div>
        <audio id="otherVideo" class="mini-video" autoplay playsinline />
      </div>
    </div>
    <div id="videos">
      <video
        v-if="!hostOnlyAudio"
        v-show="videoElements.hostVideo"
        id="hostVideo"
        class="hostVideo"
        autoplay
        playsinline
      />
      <div v-if="hostOnlyAudio">
        <div class="onlyAudio">
          <p>{{ onlyAudioMessage('hostVideo') }}</p>
          <v-icon large color="red">mdi-video-off</v-icon>
        </div>
        <audio id="hostVideo" autoplay playsinline />
      </div>
      <div v-if="!videoElements.hostVideo" class="waitMsg">
        <h1>{{ waitHeader }}</h1>
        <p>{{ waitText }}</p>
      </div>
      <div v-if="!videoElements.hostVideo" class="spinner">
        <Socket />
      </div>
      <div id="switchCamera">
        <v-btn
          :disabled="!cameraSwitchEnabled"
          icon
          large
          v-on:click="switchLocalCamera"
          color="error"
        >
          <v-icon>mdi-camera-party-mode</v-icon>
        </v-btn>
      </div>
    </div>
    <div class="overlay-buttons">
      <v-dialog
        v-if="isChatEnabled"
        v-model="chatDialog"
        fullscreen
        hide-overlay
        transition="dialog-bottom-transition"
      >
        <template v-slot:activator="{ on }">
          <v-btn class="action-button" fab color="white" v-on="on">
            <v-badge
              :content="pendingToRead"
              :value="pendingToRead"
              color="green"
            >
              <v-icon>mdi-message-draw</v-icon></v-badge
            ></v-btn
          >
        </template>
        <Chat
          :closeDialog="closeChat"
          :sendChatMessage="sendChatMessage"
          :messages="messages"
          :thereAreParticipants="thereAreParticipants"
        />
      </v-dialog>
      <v-dialog
        v-if="isFileSharingEnabled"
        v-model="fileSharingDialog"
        fullscreen
        hide-overlay
        transition="dialog-bottom-transition"
      >
        <template v-slot:activator="{ on }">
          <v-btn class="action-button" fab color="warning" v-on="on">
            <v-badge
              :content="filesPendingToReview"
              :value="filesPendingToReview"
              color="green"
            >
              <v-icon>mdi-file-document</v-icon></v-badge
            ></v-btn
          >
        </template>
        <FileSharing
          :closeDialog="closeFileSharing"
          :sendFile="sendFile"
          :showMessage="showMessage"
          :files="files"
        />
      </v-dialog>
      <v-btn
        class="action-button"
        v-on:click="switchLocalAudioTrack"
        fab
        color="primary"
      >
        <v-icon v-if="localAudioTrack.enabled">mdi-microphone</v-icon>
        <v-icon v-if="!localAudioTrack.enabled">mdi-microphone-off</v-icon>
      </v-btn>
      <v-btn
        :disabled="localOnlyAudio"
        class="action-button"
        v-on:click="switchLocalVideoTrack"
        fab
        color="primary"
      >
        <v-icon v-if="localVideoTrack.enabled">mdi-video</v-icon>
        <v-icon v-if="!localVideoTrack.enabled">mdi-video-off</v-icon>
      </v-btn>
      <v-btn
        v-if="switchDeviceButtonEnabled"
        class="action-button"
        v-on:click="switchDevice"
        fab
        color="indigo lighten-3"
      >
        <v-icon>mdi-swap-horizontal-bold</v-icon>
      </v-btn>
      <v-dialog
        v-if="
          role === 'doctor' ||
            (role === 'patient' && isPatientEndRoomEnabled === true)
        "
        v-model="closeConfirmationDialog"
        fullscreen
        hide-overlay
        transition="dialog-bottom-transition"
      >
        <template v-slot:activator="{ on }">
          <v-btn class="action-button" fab color="error" v-on="on">
            <v-icon>mdi-phone-hangup</v-icon>
          </v-btn>
        </template>
        <CloseConfirmation
          :closeDialog="closeCloseConfirmation"
          :leaveRoom="leaveRoom"
        />
      </v-dialog>
      <v-btn
        v-else
        class="action-button"
        v-on:click="leaveRoom"
        fab
        color="error"
      >
        <v-icon>mdi-phone-hangup</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { Socket } from 'vue-loading-spinner';
import TurnCredentials from '@/services/TurnCredentials.js';
import FileSharing from '@/components/FileSharing.vue';
import Chat from '@/components/Chat.vue';
import CloseConfirmation from '@/components/CloseConfirmation.vue';
import { v4 as uuidv4 } from 'uuid';
import { init } from './transcriptions/main.js';

if ('File' in self) {
  File.prototype.arrayBuffer = File.prototype.arrayBuffer || myArrayBuffer;
}
Blob.prototype.arrayBuffer = Blob.prototype.arrayBuffer || myArrayBuffer;

function myArrayBuffer() {
  return new Promise(resolve => {
    let fr = new FileReader();
    fr.onload = () => {
      resolve(fr.result);
    };
    fr.readAsArrayBuffer(this);
  });
}

const signallingMessageType = {
  OFFER: 'offer',
  ANSWER: 'answer',
  ICECANDIDATE: 'icecandidate'
};

const MAXIMUM_MESSAGE_SIZE = 65535;
const END_OF_FILE_MESSAGE = 'EOF';

export default {
  name: 'VideoCall',
  components: {
    Socket,
    FileSharing,
    Chat,
    CloseConfirmation
  },
  props: {
    userId: String,
    roomId: String,
    token: String,
    role: String,
    waitHeader: String,
    waitText: String,
    isChatEnabled: Boolean,
    isFileSharingEnabled: Boolean,
    isDoctorDisconnectEnabled: Boolean,
    isPatientEndRoomEnabled: Boolean,
    closeRoomCallback: { type: Function },
    expireRoomCallback: { type: Function },
    disconnectRoomCallback: { type: Function },
    switchDeviceCallback: { type: Function },
    showMessage: { type: Function },
    showMessageWithTimeout: { type: Function },
    showMessagePermanent: { type: Function }
  },
  watch: {
    chatDialog: function(value) {
      if (value) {
        this.pendingToRead = null;
      }
    },
    fileSharingDialog: function(value) {
      if (value) {
        this.filesPendingToReview = null;
      }
    }
  },
  data: () => ({
    fileSharingDialog: false,
    chatDialog: false,
    closeConfirmationDialog: false,
    // Contiene los identificadores de cliente (socket id) de los participantes
    // que están en la sala. Cada participante tiene asociado su rol:
    // peers[peer_client_id] = 'doctor/patient/guest'
    peers: {},
    // Para cada cliente (socket id) guarda el objeto del DOM que contiene su
    // video.
    peersVideo: {},
    // Indica si el cliente solamente tiene audio disponible, no video
    // Al facultativo no se le permite iniciar si no dispone de ambos
    localOnlyAudio: false,
    // CHECKME: No comprendo muy bien estas dos
    otherOnlyAudio: false,
    hostOnlyAudio: false,
    // MediaStreamConstraints, indica los requisitos que tenemos para empezar a
    // usar los dispositivos multimedia (audio, video, ambos, etc.)
    constraints: null,
    localVideo: undefined,
    videoElements: { hostVideo: false, otherVideo: false },
    localStream: undefined,
    localAudioTrack: { enabled: true, track: null },
    localVideoTrack: { enabled: true, track: null },
    localType: undefined,
    //??
    peerStreams: {},
    // Para cada cliente (socket id) guarda el objeto RTCPeerConnection que
    // propicia la comunicación webrtc con el otro participante
    pc: {},
    // Para cada cliente (socket id) guarda un objeto RTCDataChannel que se
    // usará para la comunicación mediante chat con el otro participante
    chat: {},
    // Almacena la configuración de conexión para el servidor stun/turn que se
    // obtiene desde el turn_credential_manager
    configuration: {},
    // Opciones usadas para lanzar el offer desde webrtc.
    // CHECKME: Se usan una sola vez y no se modifican nunca!!
    offerOptions: {
      offerToReceiveAudio: 1,
      offerToReceiveVideo: 1
    },
    // Guarda el id de cliente en la sesión del socket.
    // CHECKME: Básicamente se usan estos identificadores para todos los métodos
    // en lugar de los que generamos en la api
    clientId: null,
    // ???
    joiningRoom: false,

    peersConnected: {},
    cameraSwitchEnabled: true,
    facingMode: 'environment',
    files: [],
    messages: [],
    pendingToRead: null,
    filesPendingToReview: null,
    disposeTranscript: null
  }),
  async created() {
    console.log(`VideoCall: Inicializando sala de vídeo '${this.roomId}'`);
    this.disposeTranscript = await init();
    this.waitHeader =
      this.waitHeader !== ''
        ? this.waitHeader
        : this.$t('message.videoCall.waitHeader');
    this.waitText =
      this.waitText !== ''
        ? this.waitText
        : this.$t('message.videoCall.waitText');
    this.updateConstraints();
    if (!(await this.getUserMedia())) return;
    await this.getAudioVideo();

    await this.getStunTurnServers();

    this.joinRoom();
  },
  mounted() {
    this.localVideo = document.getElementById('localVideo');
    this.localVideo.volume = 0;
  },
  destroyed() {
    console.log(`VideoCall: Limpiando conexiones y liberando recursos.`);
    // Limpiamos las conexiones y liberamos los recursos
    Object.keys(this.peers).forEach(peer => {
      this.removePeerInfo(peer);
    });
    this.peers = {};
    this.stopLocalTracks();
    this.disposeTranscript();
    delete this.localVideoTrack;
    delete this.localAudioTrack;
    delete this.localVideo;
    delete this.localStream;
  },
  computed: {
    switchDeviceButtonEnabled: function() {
      return this.role === 'doctor' && this.isDoctorDisconnectEnabled;
    }
  },
  sockets: {
    delayNotification: function(notification) {
      const minutes = notification.minutes;
      const message = this.getDelayNotificationMessage(minutes);
      this.showMessage(message);
    },
    signallingMessage: function(response) {
      const peer = response.from;
      const type = response.type;
      const role = response.role;
      const onlyAudio = response.onlyAudio;
      const msg = response.message;
      switch (type) {
        case signallingMessageType.OFFER:
          this.peersConnected[peer] = false;
          this.addNewPeer(peer, role);
          this.checkOnlyAudio(peer, onlyAudio);
          this.createPeerConnection(peer);
          this.addLocalStream(peer);
          this.onIceCandidates(peer);
          this.onDataChannel(peer);
          this.onConnectionStateChange(peer);
          this.setRemoteDescription(peer, msg);
          this.onTrack(peer);
          this.createAnswer(peer, msg);
          break;
        case signallingMessageType.ANSWER:
          this.setRemoteDescription(peer, msg);
          this.onTrack(peer);
          break;
        case signallingMessageType.ICECANDIDATE:
          this.addCandidate(peer, msg);
          break;
      }
    },
    peerJoined: function(peerInfo) {
      if (peerInfo === undefined) return;
      const peer = peerInfo.clientId;
      const type = peerInfo.role;
      const onlyAudio = peerInfo.onlyAudio;
      console.log(
        `VideoCall: Participante ${peer} con rol ${type} se ha unido. Enviando offer.`
      );
      this.joiningRoom = false;

      this.addNewPeer(peer, type);
      this.checkOnlyAudio(peer, onlyAudio);
      this.createPeerConnection(peer);
      this.addLocalStream(peer);
      this.onIceCandidates(peer);
      // Establece los manejadores para gestionar el canal de chat y el canal de
      // compartición de ficheros
      this.onDataChannel(peer);
      this.onConnectionStateChange(peer);
      // Unicamente crea el canal de chat en participantes que ya estaban en la
      // sala cuando se une un nuevo participante. En los que se unen, se maneja
      // el canal de chat desde onDataChannel
      this.createChatChannel(peer);
      this.createOffer(peer);
    },
    // Este evento únicamente lo manda el server cuando el paciente o invitado
    // cierran la sala
    leaveRoom: function(peer) {
      console.log(
        `VideoCall: El participante ${peer} con rol ${this.peers[peer]} cerró.`
      );
      const userLeaving = this.getPeerName(peer);

      this.showMessage(
        this.$t('message.videoCall.userClosed', {
          role: userLeaving
        })
      );
      // Si el paciente cierra y puede cerrar salas, finalizamos la sala
      if (
        this.peers[peer] == 'patient' &&
        this.isPatientEndRoomEnabled === true
      ) {
        console.log(
          `VideoCall: Sala '${this.roomId}' finalizada por '${peer}' con rol '${this.peers[peer]}'.`
        );
        this.closeEndedRoom(this.$t('message.videoCall.roomClosedByPatient'));
      }
      this.removePeerInfo(peer);
    },
    peerDisconnected: function(peer) {
      console.log(`VideoCall: Participante ${peer} desconectado`);
      const userDisconnected = this.getPeerName(peer);

      this.showMessage(
        this.$t('message.videoCall.userDisconnected', {
          role: userDisconnected
        })
      );
      this.removePeerInfo(peer);
    },
    roomAboutToExpire: function(remainingMinutes) {
      this.showMessageWithTimeout(
        this.$t('message.videoCall.graceAlert', {
          remainingMinutes: remainingMinutes
        }),
        20000
      );
    },
    // Este evento únicamente lo manda el server cuando el doctor cierra la sala
    roomClosed: function() {
      console.log(
        `VideoCall: Sala '${this.roomId}' finalizada por el Facultativo`
      );
      this.closeEndedRoom(this.$t('message.videoCall.roomClosedByDoctor'));
    },
    roomExpired: function() {
      console.log(`VideoCall: Sala '${this.roomId}' ha expirado`);
      this.closeExpiredRoom(this.$t('message.videoCall.roomExpired'));
    }
  },
  methods: {
    closeFileSharing() {
      this.fileSharingDialog = false;
    },
    closeChat() {
      this.chatDialog = false;
    },
    closeCloseConfirmation() {
      this.closeConfirmationDialog = false;
    },

    thereAreParticipants() {
      return Object.keys(this.peers).length != 0;
    },
    checkOnlyAudio(peer, onlyAudio) {
      console.log(
        `VideoCall: Estableciendo onlyAudio '${onlyAudio}' al participante '${peer}'`
      );
      const videoElement = this.getElementVideo(this.peers[peer]);
      switch (videoElement) {
        case 'otherVideo':
          this.otherOnlyAudio = onlyAudio;
          break;
        case 'hostVideo':
          this.hostOnlyAudio = onlyAudio;
          break;
      }
    },
    removeOnlyAudio(peer) {
      const videoElement = this.getElementVideo(this.peers[peer]);
      switch (videoElement) {
        case 'otherVideo':
          this.otherOnlyAudio = false;
          break;
        case 'hostVideo':
          this.hostOnlyAudio = false;
          break;
      }
    },
    joinRoom() {
      console.log(`VideoCall: Uniendose a la sala '${this.roomId}'`);
      this.joiningRoom = true;
      this.$socket.client.emit(
        'joinRoom',
        {
          userId: this.userId,
          roomId: this.roomId,
          token: this.token,
          onlyAudio: this.localOnlyAudio
        },
        response => {
          // No devuelve nada si los datos enviados no son válidos (algún nulo)
          // equivale a "Invalid request"
          if (response === undefined) {
            this.closeRoom(this.$t('message.videoCall.notAllowedToJoin'));
            return;
          }
          // "Invalid request" Si no se pudo validar al participante en la API
          // "Room not ongoing" Si la sala no está en estado running
          // "Not pre joined" Si el prejoin del participante no se hizo bien
          // "Peer already in room" Si el mismo participante ya está en la sala
          if (!response.success) {
            console.log(
              `VideoCall: Error uniendose a la sala: ${response.response}`
            );
            this.closeRoom(this.$t('message.videoCall.notAllowedToJoin'));
          } else {
            const clientId = response.response.clientId;
            const role = response.response.role;
            // CHECKME: No entiendo porqué hace esto aquí: Primero hace el join
            // y después comprueba si es doctor y no tiene video para mandar un
            // mensaje diciendo que no se puede unir???. Esto puede hacer que la
            // negociación del webrtc (offer, response, icecandidate) esté ya
            // iniciada.
            if (role === 'doctor' && this.localOnlyAudio) {
              this.$socket.client.emit('unableToConnectVideo', this.roomId);
              this.closeRoom(this.$t('message.videoCall.doctorMustHaveVideo'));
            }
            console.log(
              `VideoCall: Client id assigned: ${clientId} with role '${role}'`
            );
            this.clientId = clientId;
            this.localType = role;
          }
        }
      );
    },
    switchDevice() {
      console.log(
        `VideoCall: Desconectando de sala '${this.roomId}' para cambio de dispositivo`
      );
      this.$socket.client.disconnect({
        userId: this.userId,
        roomId: this.roomId,
        token: this.token
      });
      this.endRoom(this.$t('message.videoCall.switchDevice'));
      this.switchDeviceCallback(this.$t('message.videoCall.closeRoom'));
    },
    // Ejecuta el cierre de la sala al pulsar el botón de colgar en la UI
    leaveRoom() {
      console.log(`VideoCall: ${this.role} saliendo de sala '${this.roomId}'`);
      this.$socket.client.emit('leaveRoom', this.roomId);
      if (
        this.localType == 'doctor' ||
        (this.role == 'patient' && this.isPatientEndRoomEnabled === true)
      ) {
        this.closeEndedRoom(this.$t('message.videoCall.closeRoom'));
      } else {
        this.closeRoom(this.$t('message.videoCall.closeRoom'));
      }
    },
    unableToConnectVideo() {
      this.$socket.client.emit('unableToConnectVideo', this.roomId);
      this.joiningRoom = false;
      this.closeRoom(this.$t('message.videoCall.connectionError'));
    },
    closeRoom(reason) {
      this.endRoom(reason);
      this.closeRoomCallback(reason, false);
    },
    closeEndedRoom(reason) {
      this.endRoom(reason);
      this.closeRoomCallback(reason, true);
    },
    closeExpiredRoom(reason) {
      this.endRoom(reason);
      this.expireRoomCallback(reason);
    },
    closeDisconnectedRoom(reason) {
      this.endRoom(reason);
      //this.disconnectRoomCallback(reason);
    },
    endRoom(reason) {
      console.log(
        `VideoCall: Closing room '${this.roomId}'. Reason '${reason}'`
      );
    },
    stopLocalTracks() {
      if (this.localVideoTrack && this.localVideoTrack.track) {
        this.localVideoTrack.track.stop();
      }
      if (this.localAudioTrack && this.localAudioTrack.track) {
        this.localAudioTrack.track.stop();
      }
    },
    async getLocalVideoDevices() {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices
        .filter(device => device.kind === 'videoinput')
        .map(video => video.deviceId);
    },
    async getUserMedia() {
      console.log(`VideoCall: Solicitando stream de video local`);

      this.stopLocalTracks();

      if ('mediaDevices' in navigator) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia(
            this.constraints
          );

          const localVideoDevices = await this.getLocalVideoDevices();
          console.log(
            `VideoCall: ${localVideoDevices.length} streams de videos detectados`
          );
          if (localVideoDevices.length == 1) {
            this.cameraSwitchEnabled = false;
          }

          this.localVideo.srcObject = stream;
          this.localStream = stream;
          return true;
        } catch (error) {
          console.log(`VideoCall: Error ${error} llamando a getUserMedia.`);
          if (error.name === 'NotAllowedError') {
            try {
              await this.validatePermissions();
            } catch (permissionError) {
              this.closeRoomCallback(
                this.$t('message.videoCall.permissionsDenied'),
                false
              );
            }
          } else {
            if (this.clientId) {
              this.$socket.client.emit('unableToConnectVideo', this.roomId);
              this.closeRoom(error);
            } else {
              const localVideoDevices = await this.getLocalVideoDevices();
              if (localVideoDevices.length == 0) {
                this.localOnlyAudio = true;
                this.updateConstraints();
                return await this.getUserMedia();
              } else {
                this.closeRoom(error);
              }
            }
          }
          return false;
        }
      }
    },
    async validatePermissions() {
      let validPermissions = true;
      const audioPermissions = await navigator.permissions.query({
        name: 'microphone'
      });
      if (audioPermissions.state == 'denied') {
        validPermissions = false;
        this.closeRoomCallback(
          this.$t('message.videoCall.audioPermissionDenied'),
          false
        );
      }

      const videoPermissions = await navigator.permissions.query({
        name: 'camera'
      });
      if (videoPermissions.state == 'denied') {
        console.log('VideoCall: Acceso a dispositivo de video denegado.');
        validPermissions = false;
        this.closeRoomCallback(
          this.$t('message.videoCall.videoPermissionDenied'),
          false
        );
      }

      return validPermissions;
    },
    getAudioVideo() {
      const video = this.localStream.getVideoTracks();
      const audio = this.localStream.getAudioTracks();

      console.log(`VideoCall: Pistas de Video locales: ${video.length}`);
      console.log(`VideoCall: Pistas de Audio locales: ${audio.length}`);

      if (video.length > 0) {
        console.log(
          `VideoCall: Usando dispositivo de video: ${video[0].label}`
        );
        this.localVideoTrack.track = video[0];
      }

      if (audio.length > 0) {
        console.log(
          `VideoCall: Usando dispositivo de audio: ${audio[0].label}`
        );
        this.localAudioTrack.track = audio[0];
        this.localAudioTrack.track.enabled = this.localAudioTrack.enabled;
      }
    },
    switchLocalVideoTrack() {
      this.localVideoTrack.enabled = !this.localVideoTrack.enabled;
      this.localVideoTrack.track.enabled = !this.localVideoTrack.track.enabled;
    },
    switchLocalAudioTrack() {
      this.localAudioTrack.enabled = !this.localAudioTrack.enabled;
      this.localAudioTrack.track.enabled = !this.localAudioTrack.track.enabled;
    },
    // Actualiza los requisitos que tenemos para los dispositivos multimedia
    updateConstraints() {
      this.constraints = {
        audio: true
      };
      if (!this.localOnlyAudio) {
        this.facingMode = this.facingMode == 'user' ? 'environment' : 'user';
        this.constraints = {
          ...this.constraints,
          video: {
            facingMode: { ideal: this.facingMode }
          }
        };
      }
    },
    async switchLocalCamera() {
      this.cameraSwitchEnabled = false;
      this.localVideoTrack.enabled = false;

      this.updateConstraints();
      if (!(await this.getUserMedia())) return;
      await this.getAudioVideo();
      await this.replaceTracks();
      this.localVideoTrack.enabled = true;
      this.cameraSwitchEnabled = true;
    },
    async getStunTurnServers() {
      const response = await TurnCredentials.getTurnCredentials(
        this.userId,
        this.roomId,
        this.token
      );
      this.configuration = {
        iceServers: [
          {
            urls: response.data.servers,
            username: response.data.turnCredentials.username,
            credential: response.data.turnCredentials.password
          }
        ]
      };
    },
    async replaceTracks() {
      Object.keys(this.peers).forEach(async peer => {
        const senders = this.pc[peer].getSenders();
        var videoSender = senders.find(
          sender => sender.track.kind == this.localVideoTrack.track.kind
        );

        await videoSender.replaceTrack(this.localVideoTrack.track);

        var audioSender = senders.find(
          sender => sender.track.kind == this.localAudioTrack.track.kind
        );

        await audioSender.replaceTrack(this.localAudioTrack.track);
      });
    },
    addLocalStream(peer) {
      this.localStream.getTracks().forEach(track => {
        this.pc[peer].addTrack(track, this.localStream);
      });
    },
    addNewPeer(peer, type) {
      this.peers[peer] = type;
    },
    createPeerConnection(peer) {
      this.pc[peer] = new RTCPeerConnection(this.configuration);
    },
    // Libera los recursos vinculados a un participante concreto
    removePeerInfo(peerToRemove) {
      const videoElement = this.getElementVideo(this.peers[peerToRemove]);
      this.removeOnlyAudio(peerToRemove);
      this.videoElements[videoElement] = false;
      // Cierra las pistas del participante y desconecta el objeto del DOM
      if (this.peerStreams[peerToRemove]) {
        this.peerStreams[peerToRemove]
          .getTracks()
          .forEach(track => track.stop());
        this.peersVideo[peerToRemove].srcObject = undefined;
      }
      // Eliminar los listeners de eventos
      if (this.pc[peerToRemove]) {
        this.pc[peerToRemove].ontrack = null;
        this.pc[peerToRemove].onremovetrack = null;
        this.pc[peerToRemove].onicecandidate = null;
        this.pc[peerToRemove].oniceconnectionstatechange = null;
        this.pc[peerToRemove].onsignalingstatechange = null;
        // Cierra la conexión
        this.pc[peerToRemove].close();
        delete this.pc[peerToRemove];
      }
      this.peersConnected[peerToRemove] = false;
      // Elimina los datos asociados al participante
      delete this.peersVideo[peerToRemove];
      delete this.peerStreams[peerToRemove];
      delete this.peers[peerToRemove];
    },
    async createOffer(peer) {
      console.log(
        `VideoCall: Creando Offer para participante ${peer} con role ${this.peers[peer]}`
      );
      try {
        const offer = await this.pc[peer].createOffer(this.offerOptions);
        console.log(`- Offer ${JSON.stringify(offer.sdp)}`);

        await this.pc[peer].setLocalDescription(offer);

        this.sendSignalingMessage(
          signallingMessageType.OFFER,
          this.pc[peer].localDescription,
          peer,
          this.localOnlyAudio
        );
      } catch (error) {
        console.log(
          `VideoCall: Error creando Offer para participante ${peer}: ${error}`
        );
      }
    },
    async createAnswer(peer, remoteDesc) {
      console.log(
        `VideoCall: Creando Answer para participante ${peer} con role ${this.peers[peer]}`
      );
      try {
        const answer = await this.pc[peer].createAnswer(remoteDesc);
        console.log(`- Answer ${JSON.stringify(answer.sdp)}`);

        await this.pc[peer].setLocalDescription(answer);

        this.sendSignalingMessage(
          signallingMessageType.ANSWER,
          this.pc[peer].localDescription,
          peer,
          this.localOnlyAudio
        );
      } catch (error) {
        console.log(
          `VideoCall: Error creando Answer para participante ${peer}: ${error}`
        );
      }
    },
    sendSignalingMessage(type, msg, peer, onlyAudio) {
      console.log(
        `VideoCall: Enviando ${type} mediante canal de señalización a participante ${peer}`
      );

      this.$socket.client.emit('signallingMessage', {
        room: this.roomId,
        from: this.clientId,
        role: this.localType,
        onlyAudio: onlyAudio,
        to: peer,
        type: type,
        message: msg
      });
    },
    onIceCandidates(peer) {
      this.pc[peer].onicecandidate = ({ candidate }) => {
        console.log(
          `VideoCall: Generando candidatos ICE ${JSON.stringify(candidate)}`
        );
        this.sendSignalingMessage(
          signallingMessageType.ICECANDIDATE,
          candidate,
          peer,
          this.localOnlyAudio
        );
      };
    },
    createChatChannel(peer) {
      this.chat[peer] = this.pc[peer].createDataChannel('chat');
      this.chat[peer].onmessage = this.onChatMessage(peer);
    },
    onDataChannel(peer) {
      this.pc[peer].ondatachannel = event => {
        const { channel } = event;
        channel.binaryType = 'arraybuffer';

        let dataChannelHandler = null;
        if (channel.label === 'chat') {
          this.chat[peer] = channel;
          dataChannelHandler = this.onChatMessage(peer);
        } else {
          dataChannelHandler = this.onFileMessage(channel);
        }
        channel.onmessage = dataChannelHandler;
      };
    },
    addFile(blob, title) {
      const url = window.URL.createObjectURL(blob);
      const uuid = uuidv4();
      this.files.push({ uuid, title, url, sent: false });
    },
    onChatMessage(peer) {
      return event => {
        const { data } = event;
        const peerName = this.getPeerName(peer);
        this.addChatMessage(peerName, data);
        this.maybeIncrementPendingToRead();
      };
    },
    maybeIncrementPendingToRead() {
      if (!this.chatDialog) {
        this.pendingToRead++;
      }
    },
    sendChatMessage(message) {
      this.addChatMessage(this.$t('message.videoCall.localTag'), message);
      Object.keys(this.peers).forEach(peer => {
        this.chat[peer].send(message);
      });
    },
    addChatMessage(user, content) {
      const uuid = uuidv4();
      this.messages.push({ uuid, user, content });
    },
    onFileMessage(channel) {
      var receivedBuffers = [];
      return async event => {
        const { data } = event;
        try {
          if (data !== END_OF_FILE_MESSAGE) {
            receivedBuffers.push(data);
          } else {
            const arrayBuffer = receivedBuffers.reduce((acc, arrayBuffer) => {
              const tmp = new Uint8Array(
                acc.byteLength + arrayBuffer.byteLength
              );
              tmp.set(new Uint8Array(acc), 0);
              tmp.set(new Uint8Array(arrayBuffer), acc.byteLength);
              return tmp;
            }, new Uint8Array());
            const blob = new Blob([arrayBuffer]);
            this.showMessage(
              this.$t('message.fileSharing.fileReceived', {
                fileName: channel.label
              })
            );
            this.addFile(blob, channel.label);
            receivedBuffers = [];
            channel.close();
            this.maybeIncrementFilesPendingToReview();
          }
        } catch (err) {
          console.log('VideoCall: Transferencia de fichero fallida.');
        }
      };
    },
    maybeIncrementFilesPendingToReview() {
      if (!this.fileSharingDialog) {
        this.filesPendingToReview++;
      }
    },
    onConnectionStateChange(peer) {
      // this.pc[peer].onconnectionstatechange = event => {
      this.pc[peer].onconnectionstatechange = () => {
        console.log(
          `VideoCall: El estado de conexión para el participante ${peer} ha cambiado: ${this.pc[peer].connectionState}.`
        );
        let pendingPeers;
        switch (this.pc[peer].connectionState) {
          case 'connected':
            this.peersConnected[peer] = true;
            pendingPeers = Object.keys(this.peersConnected).filter(
              peer => !this.peersConnected[peer]
            );
            if (pendingPeers.length == 0) {
              console.log(
                'VideoCall: Conexión Webrtc establecida con todos los participantes'
              );
              this.joiningRoom = false;
              this.peersConnected = {};
            }
            break;
          case 'disconnected':
          case 'closed':
            this.showMessage(
              this.$t('message.videoCall.comunicationLost', {
                role: this.getPeerName(peer)
              })
            );
            this.removePeerInfo(peer);
            this.peersConnected[peer] = false;
            break;
          case 'failed':
            if (this.joiningRoom) {
              this.unableToConnectVideo();
              this.joiningRoom = false;
              this.peersConnected = {};
              this.removePeerInfo(peer);
            }
            break;
        }
      };
    },
    async setRemoteDescription(peer, remoteDesc) {
      console.log(
        `VideoCall: Estableciendo descripción remota ${JSON.stringify(
          remoteDesc
        )} para el particpante ${peer} con role ${this.peers[peer]}.`
      );
      try {
        await this.pc[peer].setRemoteDescription(remoteDesc);
      } catch (error) {
        console.log(
          `VideoCall: Error estableciendo descripción remota para el participante ${peer}: ${error}`
        );
      }
    },
    async addCandidate(peer, candidate) {
      if (candidate == undefined) return;
      console.log(`VideoCall: Agregando candidato ICE (participante ${peer})`);
      try {
        await this.pc[peer].addIceCandidate(candidate);
      } catch (error) {
        console.log(`VideoCall: Error agregando candidato ICE: ${error}`);
      }
    },
    onTrack(peer) {
      this.pc[peer].ontrack = event => {
        console.log(
          `VideoCall: Estableciendo evento onTrack para el participante ${peer}`
        );
        const elementVideo = this.getElementVideo(this.peers[peer]);
        this.peerStreams[peer] = event.streams[0];
        this.peersVideo[peer] = document.getElementById(elementVideo);
        this.peersVideo[peer].srcObject = this.peerStreams[peer];
        this.videoElements[elementVideo] = true;
        if (elementVideo === 'hostVideo') {
          const video = this.peersVideo[peer];
          video.onresize = e => {
            console.dir(e);
            console.log(video.videoHeight + ' ' + video.videoWidth);
            const videoPortrait = video.videoHeight > video.videoWidth;
            const localPortrait = screen.height > screen.width;
            console.log(`Local portrait: ${localPortrait}`);
            console.log(`Video portrait: ${videoPortrait}`);

            video.style.width = 'auto';
            video.style.height = 'auto';
            if (localPortrait && videoPortrait) {
              video.style.width = '100%';
            } else if (!localPortrait && videoPortrait) {
              video.style.height = '100%';
            } else if (localPortrait && !videoPortrait) {
              video.style.height = '100%';
            } else if (!localPortrait && !videoPortrait) {
              video.style.width = '100%';
            }
          };
        }
      };
    },
    getElementVideo(role) {
      switch (role) {
        case 'doctor':
          return 'hostVideo';
        case 'patient':
          if (this.localType === 'doctor') {
            return 'hostVideo';
          } else {
            return 'otherVideo';
          }
        case 'guest':
          return 'otherVideo';
      }
    },
    getRoleByVideoElement(videoElement) {
      switch (videoElement) {
        case 'hostVideo':
          if (this.localType === 'doctor') {
            return 'patient';
          } else {
            return 'doctor';
          }
        case 'otherVideo':
          if (this.localType === 'guest') {
            return 'patient';
          } else {
            return 'guest';
          }
      }
    },
    getPeerName(peer) {
      return this.getPeerNameByRole(this.peers[peer]);
    },
    getPeerNameByRole(role) {
      switch (role) {
        case 'doctor':
          return this.$t('message.videoCall.doctor');
        case 'patient':
          return this.$t('message.videoCall.patient');
        case 'guest':
          return this.$t('message.videoCall.guest');
      }
    },
    onlyAudioMessage(videoElement) {
      const role = this.getRoleByVideoElement(videoElement);
      return this.getPeerNameByRole(role);
    },
    getDelayNotificationMessage(minutes) {
      return this.$t('message.delayNotification.doctorDelayMessage', {
        minutes
      });
    },
    sendFile(file) {
      Object.keys(this.peers).forEach(peer => {
        console.log(
          `VideoCall: Enviando fichero '${file.name}' a participante '${peer}'`
        );
        const dataChannel = this.pc[peer].createDataChannel(file.name);
        dataChannel.binaryType = 'arraybuffer';
        dataChannel.onopen = async () => {
          const arrayBuffer = await file.arrayBuffer();
          for (
            let i = 0;
            i < arrayBuffer.byteLength;
            i += MAXIMUM_MESSAGE_SIZE
          ) {
            dataChannel.send(arrayBuffer.slice(i, i + MAXIMUM_MESSAGE_SIZE));
          }
          dataChannel.send(END_OF_FILE_MESSAGE);
          this.showMessage(
            this.$t('message.fileSharing.fileSent', {
              fileName: file.name
            })
          );
          this.notifyFileSent(arrayBuffer.byteLength);
        };
      });
    },
    notifyFileSent(fileSize) {
      this.$socket.client.emit('fileSent', {
        userId: this.userId,
        roomId: this.roomId,
        token: this.token,
        fileSize: fileSize
      });
    }
  }
};
</script>

<style>
@media (orientation: portrait) {
  .overlay-buttons {
    position: absolute;
    bottom: 5px;
    margin: auto;
    right: 5px;
    width: 55px;
  }

  .action-button {
    margin-top: 5px;
    text-align: center;
  }
}
@media (orientation: landscape) {
  .overlay-buttons {
    position: absolute;
    bottom: 5px;
    left: calc(50vw - 155px);
  }

  .action-button {
    margin-left: 5px;
    text-align: center;
  }
}

#videos {
  font-size: 0;
  position: absolute;
  transition: all 1s;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

@media (orientation: portrait) {
  .mini-video {
    border: 1px solid gray;
    float: right;
    margin: auto;
    margin-left: 5px;
    margin-top: 10px;
    width: 140px;
    opacity: 1;
    transition: opacity 1s;
    transform: rotateY(180deg);
  }

  .mini-audio {
    border: 1px solid gray;
    float: right;
    margin: auto;
    margin-left: 5px;
    margin-top: 10px;
    width: 140px;
    opacity: 1;
    transition: opacity 1s;
    height: 185px;
  }

  #mini-videos {
    position: absolute;
    bottom: 5px;
    left: 1px;
    z-index: 1;
  }
}

@media (max-width: 900px) and (orientation: landscape) {
  .mini-video {
    border: 1px solid gray;
    float: right;
    margin: auto;
    margin-left: 5px;
    margin-top: 10px;
    width: 110px;
    opacity: 1;
    transition: opacity 1s;
    transform: rotateY(180deg);
  }

  .mini-audio {
    border: 1px solid gray;
    float: right;
    margin: auto;
    margin-left: 5px;
    margin-top: 10px;
    width: 110px;
    opacity: 1;
    transition: opacity 1s;
  }

  #mini-videos {
    position: absolute;
    bottom: 5px;
    left: 1px;
    max-width: 125px;
    z-index: 1;
  }
}

@media (min-width: 900px) and (orientation: landscape) {
  .mini-video {
    border: 1px solid gray;
    float: right;
    margin: auto;
    margin-right: 5px;
    margin-top: 10px;
    width: 180px;
    opacity: 1;
    transition: opacity 1s;
    transform: rotateY(180deg);
  }

  .mini-audio {
    border: 1px solid gray;
    float: right;
    margin: auto;
    margin-left: 5px;
    margin-top: 10px;
    width: 180px;
    opacity: 1;
    transition: opacity 1s;
  }

  #mini-videos {
    position: absolute;
    bottom: 5px;
    left: 5px;
    z-index: 1;
  }
}

.hostVideo {
  display: block;
  max-height: 100%;
  max-width: 100%;
  object-fit: cover;
  opacity: 1;
  position: absolute;
  transition: opacity 1s;
}

.onlyAudio {
  display: block;
  width: 100%;
  max-height: 100%;
  max-width: 100%;
  opacity: 1;
  text-align: center;
  position: absolute;
  top: calc(50vh - 50px);
  transition: opacity 1s;
  font-size: 11pt;
}

.miniOnlyAudio {
  display: block;
  width: 100%;
  max-height: 100%;
  max-width: 100%;
  opacity: 0.7;
  text-align: center;
  height: 100%;
  transition: opacity 1s;
  font-size: 11pt;
  background-color: white;
  padding-top: 40px;
}

.spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  height: 100%;
}

.waitMsg {
  display: block;
  width: 100%;
  max-height: 100%;
  max-width: 100%;
  opacity: 1;
  text-align: center;
  position: absolute;
  /*top: calc(45vh - 50px);*/
  top: 20%;
  transition: opacity 1s;
  font-size: 11pt;
  padding: 2pt;
}

#switchCamera {
  position: absolute;
  top: 3px;
  right: 3px;
}
</style>
